import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowDownToLine, Info, Phone} from "lucide-react";
import { formatUnixTimestamp } from "pages/Opportunities/utils/featureRule";
import CustomerCardsWithRejectedToggle from "../../components/customer_card";
import { LEVEL_2 } from "common/constants";
import { checkLevel } from "common/rules";
import { CardDetails, Milestone, TripDetail, Overview as TypeOverview } from "pages/GenericOrderDetailsPage/types/order";
import { useMutation, useQueryClient } from "react-query";
import { updateNotes } from "pages/GenericOrderDetailsPage/apis";
import { toast } from "sonner";
import { Spinner } from "components";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Overview({
    orderdetails,
    tripData,
    param,
    tempNote,
    setTempNote,
    isEditing,
    setIsEditing,
    openDetailNote,
    setTab,
  buttonState = false,
    cardList=[],
    milestones = [],
    orderType
}: {
    orderdetails: TypeOverview;
    buttonState?: boolean;
    tripData:TripDetail;
    param: any;
    note: string;
    setNote: (note: string) => void;
    tempNote: string;
    setTempNote: (tempNote: string) => void;
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
    openDetailNote: () => void;
    onCustomerClick?: (customerId: number) => void;
    setTab?: (tab: string) => void;
    cardList?: CardDetails[];
    milestones?: Milestone[];
    orderType?: string;
}) {
    const amount_breakdown = {
        'Total Payable': orderdetails?.amount_and_charges.total_payable,
        'Total Paid': orderdetails?.amount_and_charges.total_paid,        
    }
    const queryClient = useQueryClient();
    const { mutate, isLoading } = useMutation({
        mutationFn: updateNotes,
        onSuccess: () => {
            toast.success("Note updated successfully");
            setIsEditing(false);
            queryClient.invalidateQueries({ queryKey: ['orderDetails'] });
        },
        onError: () => {
            toast.error("Error updating note");
        }
    })

    const handleSaveNote = () => {
        if (tempNote) {
            mutate({
                order_id: param.id,
                notes: tempNote,
            })
        }
    }
    const cardDetails = cardList?.[0]
    if(isLoading) {
        return <Spinner/>
    }

    const checkNullDeliveryInfo = orderdetails?.delivery_info?.filter(val=>val?.uuid)

    return (
      <div className="flex md:flex-col sm:flex-col  flex-row gap-9 w-full h-full">
        <div className="space-y-4 text-sm w-[75%] text-gray-700">
          <CustomerCardsWithRejectedToggle
            product_list={orderdetails?.products}
            hasCardAssigned={cardList?.length > 0}
            setTab={setTab}
            cardDetails={cardDetails}
            milestones={milestones}
            orderType={orderType}
          />

          {orderType !== 'sell' && (
            <div className="bg-[#F9F9F9] max-w-sm p-4 rounded-xl space-y-1">
              <div className="flex items-center justify-between">
                <h5 className="text-[#141414] font-medium leading-[115%] tracking-[-3%]">LRS Status</h5>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge className={`px-3 py-1 space-x-2 rounded-[17px] text-sm ${orderdetails?.lrs_signed ? 'bg-[#E2FFE9] text-[#00A82D]' : 'bg-red-100 text-red-800'}`}>
                        <p className="">{orderdetails?.lrs_signed ? "Signed " : "Not Signed"}</p>
                        <Info className="w-4 h-4 cursor-pointer"/>
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      className="bg-black-900 text-white-A700 max-w-[200px] whitespace-normal break-words border-0"
                    >
                      <p className="text-[14px] font-medium leading-[22px]">
                        {orderdetails?.lrs_signed 
                          ? "LRS document has been signed." 
                          : "LRS document signing is pending."
                        }
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}
          {checkNullDeliveryInfo?.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 font-medium text-[#999999]">
                Delivery
                <Button
                  variant="ghost"
                  onClick={() => setTab?.("delivery")}
                  className="text-blue-500 cursor-pointer"
                >
                  <svg
                    width="15"
                    height="22"
                    viewBox="0 0 15 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clip-path="url(#clip0_5462_16103)">
                      <path
                        d="M11.3438 16.0469L10.9986 8.63098C10.9772 8.17071 10.5867 7.78027 10.1265 7.75881L2.71056 7.41369"
                        stroke="#165FE3"
                        stroke-width="1.5"
                        stroke-linecap="round"
                      />
                      <path
                        d="M2.09375 16.6641L10.1103 8.64753"
                        stroke="#165FE3"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_5462_16103">
                        <rect
                          width="14"
                          height="21"
                          fill="white"
                          transform="translate(0.09375 0.710938)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </Button>
              </div>
              {orderdetails?.delivery_info?.map((item, index) => {
                const getFullDeliveryAddress = Object.entries(
                  item?.address_detail || {}
                )
                  .filter(([key]) => key !== "id" && key !== "created_at")
                  .map(([, value]) => value)
                  .join(", ");
                return (
                  <div key={index} className="grid grid-cols-2 gap-4">
                    <div className="bg-[#F9F9F9] p-4 rounded-lg space-y-1">
                      <div>{item?.full_name}</div>
                      <div className="text-[#165FE3] flex flex-row items-center gap-2">
                        <Phone size={14} />
                        <span>{item?.phone_number || "N/A"}</span>
                      </div>
                    </div>
                    <div className="bg-[#F9F9F9] p-4 rounded-lg">
                      <p>{getFullDeliveryAddress}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {tripData && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 font-medium text-[#999999]">
                Trip
              </div>
              <div className=" flex flex-col xl:flex-row gap-5 w-full">
                <div className="flex w-full h-fit rounded-lg bg-[#F9F9F9] items-center justify-between p-4 gap-4">
                  <div className="flex items-center gap-2">
                    <svg width="17" height="15" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0.55 12.8583H16.3833V14.525H0.55V12.8583ZM16.8583 5.05833C16.6833 4.39167 15.9917 3.99167 15.325 4.175L10.9 5.35833L5.15 0L3.54167 0.425L6.99167 6.4L2.85 7.50833L1.20833 6.225L0 6.55L2.15833 10.2917C2.15833 10.2917 8.09167 8.70833 15.9667 6.6C16.6417 6.40833 17.0333 5.725 16.8583 5.05833Z" fill="black" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">Onward journey ticket</span>
                  </div>
                  {tripData?.departure?.dep_ticket?.document?.url && (
                    <a 
                      href={tripData.departure.dep_ticket.document.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <ArrowDownToLine className="text-blue-700" />
                    </a>
                  )}
                </div>

                <div className="flex bg-[#F9F9F9] rounded-lg h-fit w-full items-center justify-between p-4 gap-4">
                  <div className="flex items-center gap-2">
                    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.30264 9.88597L10.114 10.9079L14.7763 12.1579C15.4781 12.3465 16.1974 11.9298 16.3859 11.2281C16.5745 10.5263 16.1579 9.80703 15.4561 9.61846L10.7938 8.36846L8.37718 0.456185L6.67982 0V7.26315L2.32454 6.09648L1.50876 4.06139L0.236849 3.7193V8.25879L1.64473 8.63597L6.30264 9.88597ZM0 14.9123H16.6667V16.6667H0V14.9123Z" fill="black" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">Return journey ticket</span>
                  </div>
                  {tripData?.return?.rtn_ticket?.document?.url && (
                    <a 
                      href={tripData.return.rtn_ticket.document.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <ArrowDownToLine className="text-blue-700" />
                    </a>
                  )}
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20Z" fill="#666666"/>
                    </svg>
                    <span className="text-sm">
                      {!tripData?.return?.return_ticket ? (
                        tripData?.return?.return_days ? (
                          <>Back within {tripData.return.return_days} Days</>
                        ) : null
                      ) : (
                        tripData?.start_date && tripData?.end_date && (
                          <>
                            {new Date(tripData.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })} - {new Date(tripData.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </>
                        )
                      )}
                    </span>
                  </div>
                </div>

              </div>
            </div>
          )}    
          <div className="space-y-2">
            <div className="flex items-center gap-2 font-medium text-[#999999]">
              Amount & Charges
            </div>
            <div className="bg-[#FAFAFA] p-4 rounded-lg space-y-4">
              {Object.entries(amount_breakdown).map(([key, value]) => (
                <div className="flex justify-between" key={key}>
                  <div className="text-[#999999]">{key}</div>
                  <div>₹ {value || "0"}</div>
                </div>
              ))}
              <div className="flex bg-[#FF000012] justify-between text-red-600 font-medium rounded-lg py-1 px-2">
                <div className="text-[#FF0000]">Balance</div>
                <div className="text-black-900">
                  ₹ {orderdetails?.amount_and_charges.balance}
                </div>
              </div>
            </div>
          </div>
          {orderdetails?.amount_and_charges.margins_and_charges && (
            !!orderdetails.amount_and_charges.margins_and_charges
              .Partner_Margin && (
              <div className="bg-[#FAFAFA] p-4 rounded-lg space-y-4">
                <div className="flex justify-between">
                  <div className="text-[#999999]">Partner's margin</div>
                  <div>
                    {
                      orderdetails.amount_and_charges.margins_and_charges
                        .Partner_Margin
                    }
                  </div>
                </div>
              </div>
            )
          )}
        </div>
        <>
          <div className="flex flex-col w-[50%] lg:w-[25%] h-full relative space-y-4">
            <div className="h-full flex flex-col">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger
                    className="flex cursor-pointer items-center w-full md:w-auto"
                    asChild
                  >
                    <div className="flex items-center space-x-1">
                      <p className="text-[14px] font-500 md:text-base leading-[22px] md:leading-[25px] text-black-label">
                        Internal Notes
                      </p>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-gray-500"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10c0 4.418-3.582 8-8 8s-8-3.582-8-8 3.582-8 8-8 8 3.582 8 8zm-8-3a1 1 0 100 2 1 1 0 000-2zm-1 4a1 1 0 012 0v2a1 1 0 01-2 0v-2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="bg-black-900 text-white-A700 max-w-[200px] whitespace-normal break-words border-0"
                  >
                    <p className="text-[14px] font-medium md:text-base leading-[22px] md:leading-[25px]">
                      Visible to the internal team
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {!isEditing ? (
                <div className="flex items-center gap-5 w-full">
                  <div className="bg-[#FAFAFA] w-full flex items-center gap-2 py-2.5 px-2 rounded-lg text-left">
                    <div
                      className="bg-[#FAFAFA] w-full flex items-center gap-2 py-2.5 px-2 rounded-lg text-left relative"
                      style={{ border: "1px solid #D2D2D2" }}
                    >
                      <div className="flex flex-col gap-1 max-h-[300px] overflow-y-scroll overflow-x-hidden pr-6">
                        <p className="font-400 text-[12px] leading-[18px] text-[#999999]">
                          {orderdetails?.note_last_edited ? (
                            <>
                              Last edited on{" "}
                              {formatUnixTimestamp(
                                Number(orderdetails?.note_last_edited)
                              )}
                            </>
                          ) : (
                            "Click on icon to add note"
                          )}
                        </p>
                        <p className="font-normal text-base leading-[150%] break-words text-[#333333] whitespace-pre-wrap">
                          {orderdetails?.notes}
                        </p>
                      </div>

                      {!checkLevel(LEVEL_2) && (
                        <button
                          disabled={
                            buttonState
                          }
                          onClick={openDetailNote}
                          className="absolute top-[10px] right-2"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M3.33594 4.24609H2.5026C2.06058 4.24609 1.63665 4.42169 1.32409 4.73425C1.01153 5.04681 0.835938 5.47073 0.835938 5.91276V13.4128C0.835938 13.8548 1.01153 14.2787 1.32409 14.5913C1.63665 14.9038 2.06058 15.0794 2.5026 15.0794H10.0026C10.4446 15.0794 10.8686 14.9038 11.1811 14.5913C11.4937 14.2787 11.6693 13.8548 11.6693 13.4128V12.5794"
                              stroke="#165FE3"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10.8333 2.58472L13.3333 5.08472M14.4875 3.90556C14.8157 3.57735 15.0001 3.13221 15.0001 2.66806C15.0001 2.20391 14.8157 1.75876 14.4875 1.43056C14.1593 1.10235 13.7142 0.917969 13.25 0.917969C12.7858 0.917969 12.3407 1.10235 12.0125 1.43056L5 8.41806V10.9181H7.5L14.4875 3.90556Z"
                              stroke="#165FE3"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full flex flex-col gap-2">
                  <textarea
                    value={tempNote || orderdetails?.notes}
                    placeholder="Enter note"
                    className="font-normal text-base leading-[150%] border border-gray-400 rounded-lg p-4 w-full h-[250px] bg-white"
                    onChange={(e) => setTempNote(e.target.value)}
                    rows={5}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={handleSaveNote}
                      disabled={!tempNote}
                      className="text-white-A700 bg-blue-700 text-sm rounded-lg py-1.5 px-3 hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="text-gray-600 bg-gray-300 text-sm rounded-lg py-1.5 px-3 hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      </div>
    );
}
